/**
 * Leviathan - Role Form Page
 * Quasar Prime LLC
 *
 * Create/Edit Role with Permission Categories
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { ArrowLeft, Shield, ChevronDown, ChevronRight } from 'lucide-react';
import { API_URL } from '@/config/api';

interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  isSystem: boolean;
}

interface FormData {
  name: string;
  description: string;
  permissions: string[];
}

// Permission categories for UI grouping
const PERMISSION_CATEGORIES: Record<string, string[]> = {
  Dashboard: ['DASHBOARD_VIEW', 'DASHBOARD_ANALYTICS'],
  Customers: ['CUSTOMERS_VIEW', 'CUSTOMERS_CREATE', 'CUSTOMERS_EDIT', 'CUSTOMERS_DELETE'],
  Orders: ['ORDERS_VIEW', 'ORDERS_CREATE', 'ORDERS_EDIT', 'ORDERS_DELETE', 'ORDERS_APPROVE'],
  Invoices: [
    'INVOICES_VIEW',
    'INVOICES_CREATE',
    'INVOICES_EDIT',
    'INVOICES_DELETE',
    'INVOICES_SEND',
    'INVOICES_VOID',
  ],
  Inventory: ['INVENTORY_VIEW', 'INVENTORY_MANAGE', 'INVENTORY_ADJUST'],
  Tanks: ['TANKS_VIEW', 'TANKS_MANAGE', 'TANKS_READINGS'],
  Dispatch: ['DISPATCH_VIEW', 'DISPATCH_MANAGE', 'DISPATCH_ASSIGN'],
  Drivers: ['DRIVERS_VIEW', 'DRIVERS_MANAGE'],
  Vehicles: ['VEHICLES_VIEW', 'VEHICLES_MANAGE'],
  Reports: ['REPORTS_VIEW', 'REPORTS_EXPORT', 'REPORTS_FINANCIAL'],
  Users: ['USERS_VIEW', 'USERS_CREATE', 'USERS_EDIT', 'USERS_DELETE'],
  Roles: ['ROLES_VIEW', 'ROLES_MANAGE'],
  Settings: ['SETTINGS_VIEW', 'SETTINGS_MANAGE'],
  'Import/Export': ['IMPORT_DATA', 'EXPORT_DATA'],
};

// Role templates for quick setup
const ROLE_TEMPLATES: Record<string, { name: string; description: string; permissions: string[] }> =
  {
    Administrator: {
      name: 'Administrator',
      description: 'Full system access',
      permissions: Object.values(PERMISSION_CATEGORIES).flat(),
    },
    Dispatcher: {
      name: 'Dispatcher',
      description: 'Manage orders and dispatch',
      permissions: [
        'DASHBOARD_VIEW',
        'CUSTOMERS_VIEW',
        'ORDERS_VIEW',
        'ORDERS_CREATE',
        'ORDERS_EDIT',
        'DISPATCH_VIEW',
        'DISPATCH_MANAGE',
        'DISPATCH_ASSIGN',
        'DRIVERS_VIEW',
        'VEHICLES_VIEW',
        'TANKS_VIEW',
        'TANKS_READINGS',
      ],
    },
    Driver: {
      name: 'Driver',
      description: 'View assigned deliveries',
      permissions: [
        'DASHBOARD_VIEW',
        'ORDERS_VIEW',
        'DISPATCH_VIEW',
        'TANKS_VIEW',
        'TANKS_READINGS',
      ],
    },
    Accountant: {
      name: 'Accountant',
      description: 'Financial and invoice management',
      permissions: [
        'DASHBOARD_VIEW',
        'DASHBOARD_ANALYTICS',
        'CUSTOMERS_VIEW',
        'ORDERS_VIEW',
        'INVOICES_VIEW',
        'INVOICES_CREATE',
        'INVOICES_EDIT',
        'INVOICES_DELETE',
        'INVOICES_SEND',
        'INVOICES_VOID',
        'REPORTS_VIEW',
        'REPORTS_EXPORT',
        'REPORTS_FINANCIAL',
      ],
    },
    Sales: {
      name: 'Sales',
      description: 'Customer and order management',
      permissions: [
        'DASHBOARD_VIEW',
        'CUSTOMERS_VIEW',
        'CUSTOMERS_CREATE',
        'CUSTOMERS_EDIT',
        'ORDERS_VIEW',
        'ORDERS_CREATE',
        'ORDERS_EDIT',
        'INVOICES_VIEW',
        'REPORTS_VIEW',
      ],
    },
  };

export default function RoleForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    permissions: [],
  });

  const [existingRole, setExistingRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(
    Object.keys(PERMISSION_CATEGORIES)
  );

  useEffect(() => {
    if (isEdit) {
      fetchRole();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchRole = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/roles/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch role');
      const data = await response.json();
      const role: Role = data.role;
      setExistingRole(role);
      setFormData({
        name: role.name,
        description: role.description || '',
        permissions: role.permissions || [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load role');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const url = isEdit ? `${API_URL}/api/users/roles/${id}` : `${API_URL}/api/users/roles`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      navigate('/admin/roles');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (templateName: string) => {
    const template = ROLE_TEMPLATES[templateName];
    if (template) {
      setFormData({
        name: template.name,
        description: template.description,
        permissions: [...template.permissions],
      });
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const togglePermission = (permission: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const toggleCategoryAll = (category: string) => {
    const categoryPerms = PERMISSION_CATEGORIES[category];
    if (!categoryPerms) return;
    const allSelected = categoryPerms.every((p) => formData.permissions.includes(p));

    setFormData((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((p) => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])],
    }));
  };

  const selectAll = () => {
    setFormData((prev) => ({
      ...prev,
      permissions: Object.values(PERMISSION_CATEGORIES).flat(),
    }));
  };

  const clearAll = () => {
    setFormData((prev) => ({
      ...prev,
      permissions: [],
    }));
  };

  const getPermissionLabel = (permission: string): string => {
    return permission
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const isSystemRole = existingRole?.isSystem;
  const allPermissions = Object.values(PERMISSION_CATEGORIES).flat();
  const selectedCount = formData.permissions.length;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/admin/roles" className="text-slate-400 hover:text-slate-200">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 via-blue-500 to-amber-500" />
          <h1 className="text-3xl font-bold text-slate-100 pl-6">
            {isEdit ? 'Edit Role' : 'Create Role'}
          </h1>
          <p className="text-slate-400 text-sm pl-6 mt-1">
            {isEdit
              ? isSystemRole
                ? 'System roles can only have permissions modified'
                : 'Modify role details and permissions'
              : 'Define a new role with specific permissions'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-slate-800 border border-slate-700 p-6">
          <div className="absolute top-0 left-0 w-2 h-2 border-l-2 border-t-2 border-amber-500" />
          <div className="absolute top-0 right-0 w-2 h-2 border-r-2 border-t-2 border-amber-500" />

          <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center space-x-2">
            <Shield className="w-5 h-5 text-amber-500" />
            <span>Role Information</span>
          </h3>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Role Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-slate-100 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                placeholder="e.g., Dispatcher, Accountant"
                required
                disabled={isSystemRole}
              />
              {isSystemRole && (
                <p className="text-xs text-amber-500 mt-1">System role names cannot be changed</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-slate-100 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                placeholder="Brief description of this role's responsibilities"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Role Templates */}
        {!isEdit && (
          <div className="bg-slate-800 border border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-100 mb-4">Quick Templates</h3>
            <div className="flex flex-wrap gap-2">
              {Object.keys(ROLE_TEMPLATES).map((templateName) => (
                <button
                  key={templateName}
                  type="button"
                  onClick={() => applyTemplate(templateName)}
                  className="px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 hover:border-amber-500/50 transition-colors"
                >
                  {templateName}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Permissions */}
        <div className="bg-slate-800 border border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-100">
              Permissions ({selectedCount}/{allPermissions.length})
            </h3>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={selectAll}
                className="text-sm text-amber-500 hover:text-amber-400"
              >
                Select All
              </button>
              <span className="text-slate-600">|</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-sm text-slate-400 hover:text-slate-300"
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {Object.entries(PERMISSION_CATEGORIES).map(([category, permissions]) => {
              const selectedInCategory = permissions.filter((p) =>
                formData.permissions.includes(p)
              ).length;
              const allInCategorySelected = selectedInCategory === permissions.length;
              const isExpanded = expandedCategories.includes(category);

              return (
                <div key={category} className="border border-slate-700 rounded">
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-700/50"
                    onClick={() => toggleCategory(category)}
                  >
                    <div className="flex items-center space-x-3">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                      <span className="font-medium text-slate-200">{category}</span>
                      <span className="text-sm text-slate-500">
                        ({selectedInCategory}/{permissions.length})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategoryAll(category);
                      }}
                      className={`text-xs px-2 py-1 border transition-colors ${
                        allInCategorySelected
                          ? 'border-amber-500 text-amber-500 hover:bg-amber-500/10'
                          : 'border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      {allInCategorySelected ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {permissions.map((permission) => (
                        <label
                          key={permission}
                          className="flex items-center gap-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission)}
                            onChange={() => togglePermission(permission)}
                            className="w-4 h-4 text-amber-500 bg-slate-900 border-slate-600 rounded focus:ring-amber-500"
                          />
                          <span className="text-sm text-slate-300">
                            {getPermissionLabel(permission)}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Link
            to="/admin/roles"
            className="px-6 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEdit ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </form>
    </div>
  );
}
